import useThemeColors from '@/hooks/useThemeColors';
import clsx from 'clsx';
import React, { forwardRef } from 'react';
import InputError from './InputError';

interface TextInputProps {
    label?: string;
    id?: string;
    name: string;
    value: string | number;
    onChange: (value: string) => void;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    type?: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    autoFocus?: boolean;
    className?: string;
    error?: string | string[] | null;
    helpText?: React.ReactNode;
    maxLength?: number;
    min?: number;
    max?: number;
    step?: number;
}

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
    (
        {
            label,
            id,
            name,
            value,
            onChange,
            onBlur,
            type = 'text',
            placeholder,
            required = false,
            disabled = false,
            readOnly = false,
            autoFocus = false,
            className = '',
            error,
            helpText,
            maxLength,
            min,
            max,
            step,
        },
        ref
    ) => {
        const colors = useThemeColors();

        return (
            <div className={`${className}`}>
                {label && (
                    <label
                        htmlFor={id || name}
                        className="mb-1 block text-sm font-medium text-gray-500 dark:text-neutral-400"
                    >
                        {label}
                        {required && (
                            <span className="ml-1 text-red-500">*</span>
                        )}
                    </label>
                )}

                <input
                    ref={ref}
                    id={id || name}
                    name={name}
                    type={type}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={onBlur}
                    placeholder={placeholder}
                    disabled={disabled}
                    readOnly={readOnly}
                    autoFocus={autoFocus}
                    maxLength={maxLength}
                    min={min}
                    max={max}
                    step={step}
                    className={clsx(
                        'w-full rounded-lg border bg-white/10 px-3 py-2 text-sm text-gray-900 [box-shadow:0_3px_10px_rgba(0,0,0,0.22),0_1px_4px_rgba(0,0,0,0.15)] backdrop-blur-sm transition-colors placeholder:text-gray-400 focus:ring-2 focus:outline-none dark:bg-white/10 dark:text-white dark:placeholder:text-neutral-500',
                        error
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                            : `border-white/50 dark:border-white/10 ${colors.inputFocus}`,
                        disabled && 'cursor-not-allowed opacity-60',
                        readOnly && 'opacity-70'
                    )}
                />

                {helpText && !error && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-neutral-400">
                        {helpText}
                    </p>
                )}

                <InputError error={error} />
            </div>
        );
    }
);

TextInput.displayName = 'TextInput';

export default TextInput;
